const COMPONENT_NAME = "web-intl";

const SHOULD_LOG = true;
const logWarn = (elem, msg) => SHOULD_LOG && console.warn(COMPONENT_NAME, msg, elem);
const logError = (elem, msg) => SHOULD_LOG && console.error(COMPONENT_NAME, msg, elem);

const REDUCED_MOTION_MS = 5000;

const MS_PER_SECOND = 1000;
const MS_PER_MINUTE = MS_PER_SECOND * 60;
const MS_PER_HOUR = MS_PER_MINUTE * 60;
const MS_PER_DAY = MS_PER_HOUR * 24;
const MS_PER_WEEK = MS_PER_DAY * 7;
const MS_PER_MONTH = MS_PER_DAY * 30;
const MS_PER_YEAR = MS_PER_DAY * 365;

const MS_UNIT_MAP = {
  second: MS_PER_SECOND,
  minute: MS_PER_MINUTE,
  hour: MS_PER_HOUR,
  day: MS_PER_DAY,
  week: MS_PER_WEEK,
  month: MS_PER_MONTH,
  year: MS_PER_YEAR,
};

const DURATION_UNIT_MAP = {
  years: MS_PER_YEAR,
  months: MS_PER_MONTH,
  weeks: MS_PER_WEEK,
  days: MS_PER_DAY,
  hours: MS_PER_HOUR,
  minutes: MS_PER_MINUTE,
  seconds: MS_PER_SECOND,
  milliseconds: 1,
};
const VALID_DURATION_UNITS = new Set(Object.keys(DURATION_UNIT_MAP));

const formatterCache = new Map();
const getCacheKey = (Constructor, opts) => `${Constructor.name}::${JSON.stringify(opts)}`;
const getCachedIntlApi = (Constructor, apiOpts = false) => {
  const key = getCacheKey(Constructor, apiOpts);
  let formatter = formatterCache.get(key);
  if (!formatter) {
    const fallbackForDisplayNames = Constructor === Intl.DisplayNames && { type: "language" };
    const { locale, ...opts } = apiOpts || fallbackForDisplayNames;
    formatter = new Constructor(locale, opts);
    formatterCache.set(key, formatter);
  }
  const opts = formatter.resolvedOptions();
  return { formatter, opts };
};
const getFormatter = (Constructor) => {
  const defaults = getCachedIntlApi(Constructor);
  return (lang, { ...opts }) => {
    // For DateTimeFormat, don't merge individual date/time components if style options are present
    const keySkipper = skipKeys(Constructor, opts);
    const cacheOpts = { locale: lang, ...opts };
    for (const key in defaults.opts) {
      if (!(key in cacheOpts) && !keySkipper(key)) cacheOpts[key] = defaults.opts[key];
    }
    if (objectIsEqual(cacheOpts, defaults.opts)) return defaults.formatter;
    return getCachedIntlApi(Constructor, cacheOpts).formatter;
  };
};

const skipKeys = (Constructor, opts) => {
  const isDateTimeFormat = Constructor === Intl.DateTimeFormat;
  if (!isDateTimeFormat) return () => false;
  const hasStyleOptions = isDateTimeFormat && (opts.dateStyle || opts.timeStyle);
  const skipKeys =
    hasStyleOptions &&
    "year,month,day,hour,minute,second,weekday,era,dayPeriod,timeZoneName,fractionalSecondDigits";
  const skipSet = new Set(skipKeys ? skipKeys.split(",") : []);
  return (key) => skipSet.has(key);
};

const isDefined = (val) => val !== null && val !== undefined;

const Attr = (elem) => {
  const api = {
    // Get single attribute
    has: (key) => elem.hasAttribute(key),
    get: (key) => elem.getAttribute(key),
    keys: () => [...elem.attributes].map((attr) => attr.name),
    getAll: (...keys) =>
      Object.fromEntries((keys.length ? keys : api.keys()).map((key) => [key, api.get(key)])),
    set: (...args) => {
      const [key, value] = args;
      if (args.length === 1) return (val) => api.set(key, val); // Curried
      if (api.get(key) === value) return api; // No change
      if (value === undefined) {
        elem.removeAttribute(key);
      } else {
        elem.setAttribute(key, value);
      }
      return api; // Chain
    },
    setAll: (obj) => {
      Object.entries(obj).forEach(([k, v]) => api.set(k, v));
      return api;
    },
    update: (key, fn) => api.set(key, fn(api.get(key))),
    updateAll: (obj) => {
      Object.entries(obj).forEach(([k, fn]) => api.update(k, fn));
      return api;
    },
    remove: (...keys) => {
      keys.forEach((key) => api.has(key) && elem.removeAttribute(key));
      return api;
    },
    clear: () => {
      api.keys().forEach(api.remove);
      return api;
    },
    element: () => elem,
  };
  return api;
};
const indexedMap = (list) => new Map(list.map((item, index) => [index, item]));
const toCamelCase = (str) => str.replace(/-([a-z])/g, (_, letter) => letter.toUpperCase());
const getLang = (elem) => Attr(elem.closest("[lang]")).get("lang") || navigator.language;
const ATTR_REMAP = { intlStyle: "style", intlType: "type" };
const remapKeys = (obj) => {
  for (const [from, to] of Object.entries(ATTR_REMAP)) {
    if (from in obj) { obj[to] = obj[from]; delete obj[from]; }
  }
  return obj;
};
const getData = (elem) => remapKeys({ lang: getLang(elem), ...camelizeObj(Attr(elem).getAll()) });
const getHtml = (elem) => elem.outerHTML;
const getHtmls = (list) => [...list].map(getHtml).join("");
const getSlots = (elem) => [...elem.children].filter((child) => Attr(child).has("slot"));
const getSlotMap = (slots) => new Map(slots.map((slot) => [slot.getAttribute("slot"), slot]));
const getFingerprint = (elem) => {
  // Efficient cache key based on attributes and slot structure instead of expensive outerHTML
  const attrs = Attr(elem).getAll();
  const lang = getLang(elem); // Include resolved language in fingerprint
  const slots = getSlots(elem);
  const toFingerprint = (s) => `${Attr(s).get("slot")}:${s.textContent.length}`;
  const slotInfo = slots.map(toFingerprint).sort().join(",");
  return `${lang}|${JSON.stringify(attrs)}|${slots.length}|${slotInfo}`;
};

const convertToDate = (val) => {
  let date = new Date(isNaN(val) ? val : Number(val));
  if (isNaN(date.getTime())) date = new Date();
  return date;
};

// if the performance of this becomes an issue, we can do a deep comparison instead
const objectIsEqual = (a, b) => JSON.stringify(a) === JSON.stringify(b);
const mapObj = (obj, fn) => Object.fromEntries(Object.entries(obj).map(fn));
const camelizeObj = (obj) => mapObj(obj, ([k, v]) => [toCamelCase(k), v]);
const partition = (fn) => (items) =>
  items.reduce((acc, item) => {
    acc[!!fn(item)].push(item);
    return acc;
  }, { true: [], false: [] });

const calculateRelativeTimeUnit = (date, now = new Date()) => {
  const diff = date - now;
  const absDiff = Math.abs(diff);
  if (absDiff >= MS_PER_YEAR) return { unit: "year", value: Math.floor(diff / MS_PER_YEAR) };
  if (absDiff >= MS_PER_MONTH) return { unit: "month", value: Math.floor(diff / MS_PER_MONTH) };
  if (absDiff >= MS_PER_WEEK) return { unit: "week", value: Math.floor(diff / MS_PER_WEEK) };
  if (absDiff >= MS_PER_DAY) return { unit: "day", value: Math.floor(diff / MS_PER_DAY) };
  if (absDiff >= MS_PER_HOUR) return { unit: "hour", value: Math.floor(diff / MS_PER_HOUR) };
  if (absDiff >= MS_PER_MINUTE) return { unit: "minute", value: Math.floor(diff / MS_PER_MINUTE) };
  return { unit: "second", value: Math.floor(diff / MS_PER_SECOND) };
};

const setVisibility = (slotElem, shouldShow = false) =>
  Attr(slotElem).setAll(visibilityAttributes(shouldShow));

const visibilityAttributes = (shouldShow = false) => ({
  // add or remove visibility attributes
  hidden: shouldShow ? undefined : "",
  "aria-hidden": shouldShow ? undefined : "true",
  inert: shouldShow ? undefined : "",
});

const copySlotAttributes = (source, target) =>
  Attr(target).setAll({ ...Attr(source).getAll(), ...visibilityAttributes(true) });

const getElementSortValue = (elem, sortBy) =>
  Attr(elem).get("sort-value") ?? elem[sortBy] ?? Attr(elem).get(sortBy) ?? elem.textContent;

const elementSorter = (collator, sortBy) => (a, b) =>
  collator.compare(getElementSortValue(a, sortBy), getElementSortValue(b, sortBy));

const getExpandedFormat = ({ lang, opts, formatExpanded }) => {
  const { style, dateStyle, timeStyle, notation } = opts;
  const isAbbreviated =
    [style, dateStyle, timeStyle].includes("short") || style === "narrow" || notation === "compact";
  if (!isAbbreviated) return null;
  const extendedOpts = {
    locale: lang,
    ...opts, // inherit opts
    // Replace abbreviated options with expanded ones
    notation: "standard",
    compactDisplay: "long",
    dateStyle: "long",
    timeStyle: "long",
  };
  // Only override style if it was abbreviated (short/narrow)
  // Don't override for NumberFormat styles like "decimal", "currency", "percent", "unit"
  if (["short", "narrow"].includes(style)) extendedOpts.style = "long";

  return formatExpanded(lang, extendedOpts);
};

const shouldShowExpander = (elem, abbreviated, full) => {
  if (!abbreviated || !full || abbreviated === full) return false;
  if (elem.closest("button, a, summary, details")) return false;
  return true;
};

const wrapWithExpander = (elem, abbreviatedElem, fullText) => {
  const abbreviatedText = abbreviatedElem.textContent.trim();
  if (!shouldShowExpander(elem, abbreviatedText, fullText)) return abbreviatedElem;

  // Attr API automatically avoids redundant mutations
  Attr(elem).set("full-value", fullText).set("abbreviated", "true");
  return createElement("details", { "aria-label": "Show full format" }, [
    createElement("summary", {}, [abbreviatedElem]),
    createElement("span", {}, fullText),
  ]);
};

const maybeExpand = (elem, contentElem, lang, opts, formatExpanded) => {
  // Early exit if no-expand attribute is set
  if (Attr(elem).has("no-expand")) return contentElem;
  // Check if content should be expanded
  const fullValue = getExpandedFormat({ lang, opts, formatExpanded });
  // Wrap with expander UI if abbreviated
  return fullValue ? wrapWithExpander(elem, contentElem, fullValue) : contentElem;
};

const createElement = (tag, attrs = {}, content = null) => {
  const element = document.createElement(tag);
  Attr(element).setAll(attrs);
  if (!content) return element;
  if (typeof content === "string") {
    element.textContent = content;
    return element;
  }
  if (Array.isArray(content)) {
    element.append(...content);
    return element;
  }
  logError(element, `Invalid "createElement" content type: ${JSON.stringify(content)}`);
  return element;
};
const createDataElement = (contents, value) => createElement("data", { value }, contents);
const createTimeElement = (contents, datetime) => createElement("time", { datetime }, contents);

const createDataElementFromParts = (slots, parts, value) =>
  createDataElement(mapPartsToElements(slots, parts), value);

const createDynamicElement = ({ value, ...rest }) => {
  const elem = createElement("span", {}, value);
  // Use Attr API to avoid needless mutations
  Attr(elem).setAll(rest);
  return elem;
};

const mapPartsToElements = (slots, parts) => {
  slots.forEach((slot) => setVisibility(slot));
  const slotMap = getSlotMap(slots);
  return parts.map((part) => {
    const [userSlot, outputElem] = [slotMap.get(part.type), createDynamicElement(part)];
    if (userSlot) copySlotAttributes(userSlot, outputElem);
    return outputElem;
  });
};

const createTimeElementFromParts = (slots, instance, datetimeValue, ...formatArgs) => {
  const parts = instance.formatToParts(...(formatArgs.length ? formatArgs : [datetimeValue]));
  const contents = mapPartsToElements(slots, parts);
  return createTimeElement(contents, datetimeValue.toISOString());
};

const getMillisecondsForUnit = (value, unit) => value * (MS_UNIT_MAP[unit] || 0);

const parseDurationUnits = (elem, includeStr, excludeStr) => {
  const errors = [`All valid units: ${[...VALID_DURATION_UNITS].join(", ")}.`];
  const parseUnit = (key, val) => {
    if (!val) return;
    const units = val.split(",").map((u) => u.trim());
    const unitPartition = partition((u) => VALID_DURATION_UNITS.has(u))(units);
    if (unitPartition.false.length > 0)
      errors.push(`Invalid unit(s) in unit-${key}: ${unitPartition.false.join(", ")}.`);
    return new Set(unitPartition.true);
  };
  if (errors.length > 1) logError(elem, errors.join(" "));
  return { include: parseUnit("include", includeStr), exclude: parseUnit("exclude", excludeStr) };
};

const calculateDistributedDurationUnits = (ms, unitsConfig = {}) => {
  const { include, exclude } = unitsConfig;

  // Determine which units to use
  const useUnits = [...VALID_DURATION_UNITS].filter((u) => {
    if (include && !include.has(u)) return false;
    if (exclude && exclude.has(u)) return false;
    return true;
  });

  const absMs = Math.abs(ms);
  let remaining = absMs;
  const result = {};
  for (const unit of useUnits) {
    const unitMs = DURATION_UNIT_MAP[unit];
    const value = Math.floor(remaining / unitMs);
    if (value > 0) result[unit] = value; // Only include non-zero values
    remaining = remaining % unitMs;
  }
  return result;
};

const getDurationValue = (elem) => {
  const { value, unitInclude, unitExclude, ...opts } = getData(elem);
  const ms = Number(value);
  const hasValidMs = isDefined(value) && !isNaN(ms);

  // Only extract actual duration unit properties from opts
    const accUnits = (acc, cur) => {
      if (isDefined(opts[cur])) acc[cur] = opts[cur];
      return acc;
    };
  const explicitUnits = Object.keys(DURATION_UNIT_MAP).reduce(accUnits, {});
  const hasExplicitUnits = Object.keys(explicitUnits).length > 0;

  if (hasExplicitUnits && hasValidMs)
    logWarn(elem, "Both explicit units and value attribute provided. Using explicit unit values.");

  // Priority: explicit units > value attribute
  if (hasExplicitUnits) {
    return Object.entries(explicitUnits).reduce(
      (acc, [unit, val]) => {
        acc[unit] = Number(val);
        return acc;
      },
      {},
    );
  }
  if (!isDefined(value)) {
    logWarn(elem, "No value attribute or explicit units were provided for DurationFormat");
    return; // eager exit
  }
  if (!hasValidMs) {
    logError(elem, `Invalid value for DurationFormat: "${value}". Value must be ms.`);
    return; // eager exit
  }

  // Parse unit filtering configuration and convert milliseconds to distributed duration object
  return calculateDistributedDurationUnits(ms, parseDurationUnits(elem, unitInclude, unitExclude));
};

const syncUpdate = (elem) => {
  const shouldSync = Attr(elem).has("sync");
  Attr(elem).setAll({
    // set or remove aria-live and aria-atomic for screen readers
    ["aria-live"]: shouldSync ? "polite" : undefined,
    ["aria-atomic"]: shouldSync ? "true" : undefined,
  });
  if (!shouldSync || !elem.isConnected) return; // no sync attr or not connected, no further action needed
  const reduceMotion =
    window.matchMedia("(prefers-reduced-motion: reduce)").matches && REDUCED_MOTION_MS;
  const interval = Math.max(Number(Attr(elem).get("sync")) || 0, reduceMotion || 0);
  interval // use setTimeout for numeric intervals
    ? setTimeout(() => syncUpdate(elem), interval)
    : requestAnimationFrame(() => syncUpdate(elem));
};

const apiHandlers = {
  Collator: (elem, slots) => {
    const { lang, _value, reverse, sortBy, ...opts } = getData(elem);
    const instance = getFormatter(Intl.Collator)(lang, opts);
    const sorted = [...slots].sort(elementSorter(instance, sortBy));
    return isDefined(reverse) ? sorted.reverse() : sorted;
  },
  DateTimeFormat: (elem, slots) => {
    const { lang, value, start, end, ...opts } = getData(elem);
    if (isDefined(value) && isDefined(start) && isDefined(end)) {
      logWarn(elem, "All 3 attrs (value,start,end) provided. Using start/end.");
    } else if (!isDefined(value) && !isDefined(start) && !isDefined(end)) {
      logWarn(elem, "No value/start/end provided. Using current time.");
    }
    const formatGetter = getFormatter(Intl.DateTimeFormat);
    const instance = formatGetter(lang, opts);
    const date = convertToDate(value);

    const hasRange = isDefined(start) || isDefined(end);
    if (!hasRange) {
      const formatExpanded = (lang, expandedOpts) => formatGetter(lang, expandedOpts).format(date);
      const timeElem = createTimeElementFromParts(slots, instance, date);
      return [maybeExpand(elem, timeElem, lang, opts, formatExpanded)];
    }

    const [startDate, endDate] = [convertToDate(start), convertToDate(end)];
    const datetimeInterval = `${startDate.toISOString()}/${endDate.toISOString()}`;
    const formatExpanded = (lang, expandedOpts) =>
      formatGetter(lang, expandedOpts).formatRange(startDate, endDate);
    const parts = instance.formatRangeToParts(startDate, endDate);
    const mappedElements = mapPartsToElements(slots, parts);
    const timeElem = createElement("time", { datetime: datetimeInterval }, mappedElements);
    return [maybeExpand(elem, timeElem, lang, opts, formatExpanded)];
  },
  DisplayNames: (elem) => {
    const { lang, value, ...opts } = getData(elem);
    if (!isDefined(value)) return [];
    const formatGetter = getFormatter(Intl.DisplayNames);
    const formatExpanded = (lang, expandedOpts) => formatGetter(lang, expandedOpts).of(value);
    const dataElem = createDataElement(formatGetter(lang, opts).of(value), value);
    return [maybeExpand(elem, dataElem, lang, opts, formatExpanded)];
  },
  DurationFormat: (elem, slots) => {
    const { lang, value, _unitInclude, _unitExclude, ...opts } = getData(elem);
    const durationValue = getDurationValue(elem);
    if (!durationValue) {
      logError(elem, "DurationFormat failed: Check value and unit attributes");
      return [];
    }
    // Filter out duration unit properties from options
    const accKeys = (acc, [k, v]) => {
      if (!isDefined(DURATION_UNIT_MAP[k])) acc[k] = v;
      return acc;
    };
    const formatterOpts = Object.entries(opts).reduce(accKeys, {});

    const formatGetter = getFormatter(Intl.DurationFormat);
    const instance = formatGetter(lang, formatterOpts);

    const formatExpanded = (lang, expandedOpts) =>
      formatGetter(lang, expandedOpts).format(durationValue);
    const dataElem = createDataElementFromParts(
      slots,
      instance.formatToParts(durationValue),
      typeof value === "string" ? value : JSON.stringify(durationValue),
    );

    return [maybeExpand(elem, dataElem, lang, formatterOpts, formatExpanded)];
  },
  ListFormat: (elem, allSlots) => {
    const { true: _literals, false: elements } = partition((s) => Attr(s).get("slot") === "literal")(
      allSlots,
    );
    if (elements.length === 0) return [];
    const { lang, _value, ...opts } = getData(elem);
    const formatGetter = getFormatter(Intl.ListFormat);
    const instance = formatGetter(lang, opts);

    // Create map from identifier to element
    const elementMap = indexedMap(elements);
    const parts = instance.formatToParts([...elementMap.keys().map(String)]);

    // Build the interleaved list of elements and connectors
    const switches = {
      element: (part) => elementMap.get(Number(part.value)),
      literal: (part) => createDynamicElement(part),
    };
    // when replacing children, elements will reorder.
    return parts.map((part) => switches[part.type](part));
  },
  NumberFormat: (elem, slots) => {
    const { lang, value, ...opts } = getData(elem);
    if (!isDefined(value)) return [];
    const numValue = Number(value);
    const formatGetter = getFormatter(Intl.NumberFormat);
    const instance = formatGetter(lang, opts);
    const formatExpanded = (lang, expandedOpts) =>
      formatGetter(lang, expandedOpts).format(numValue);
    const dataElem = createDataElementFromParts(slots, instance.formatToParts(numValue), value);

    // For currency, wrap with aria-label containing full currency name
    if (opts.style === "currency" && opts.currency) {
      const formatted = instance.format(numValue);
      const currencyName = getFormatter(Intl.DisplayNames)(lang, { type: "currency" }).of(opts.currency);
      const wrapper = createElement("span", { "aria-label": `${formatted} ${currencyName}` }, [
        dataElem,
      ]);
      return [maybeExpand(elem, wrapper, lang, opts, formatExpanded)];
    }

    return [maybeExpand(elem, dataElem, lang, opts, formatExpanded)];
  },
  PluralRules: (elem, slots) => {
    const { lang, value, start, end, ...opts } = getData(elem);
    const hasRange = isDefined(start) || isDefined(end);
    if (!hasRange && !isDefined(value)) return [];
    if (isDefined(value) && isDefined(start) && isDefined(end))
      logWarn(elem, "All 3 attrs (value,start,end) provided. Using start/end.");

    const instance = getFormatter(Intl.PluralRules)(lang, opts);
    const category = hasRange
      ? instance.selectRange(Number(start ?? value), Number(end ?? value))
      : instance.select(Number(value));

    // Show only the slot matching the selected category
    slots.forEach((child) => setVisibility(child, child.getAttribute("slot") === category));
    return []; // no dynamic content to add
  },
  RelativeTimeFormat: (elem, slots) => {
    const { lang, value, unit, sync, ...opts } = getData(elem);
    isDefined(sync) && syncUpdate(elem);

    const formatGetter = getFormatter(Intl.RelativeTimeFormat);
    const instance = formatGetter(lang, opts);

    let auto, absoluteDate;
    if (isDefined(unit)) {
      const numValue = Number(value);
      auto = { unit, value: numValue };
      absoluteDate = new Date(Date.now() + getMillisecondsForUnit(numValue, unit));
    } else {
      const targetDate = convertToDate(value);
      auto = calculateRelativeTimeUnit(targetDate);
      absoluteDate = targetDate;
    }
    const formatExpanded = (lang, expandedOpts) =>
      formatGetter(lang, expandedOpts).format(auto.value, auto.unit);
    const timeElem = createTimeElementFromParts(
      slots,
      instance,
      absoluteDate,
      auto.value,
      auto.unit,
    );

    return [maybeExpand(elem, timeElem, lang, opts, formatExpanded)];
  },
};

const init = () => {
  // Eager exit if component already defined
  if (customElements.get(COMPONENT_NAME)) {
    console.warn(`${COMPONENT_NAME} is already defined. Skipping redefinition.`);
    return;
  }

  // Define custom element
  customElements.define(
    COMPONENT_NAME,
    class extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        this._observer = new MutationObserver(() => this.update());
        this._observer.observe(this, { attributes: true, childList: true });
        this.update();
      }

      disconnectedCallback() {
        this._observer?.disconnect();
      }

      update() {
        if (this._updatePending) return;
        this._updatePending = true;
        queueMicrotask(() => {
          if (!this.isConnected) {
            this._updatePending = false;
            return; // eager exit if disconnected
          }
          const currentFingerprint = getFingerprint(this);
          if (this._cache !== currentFingerprint) {
            this.render();
            // Update cache immediately after render (avoid timing window)
            if (this.isConnected) this._cache = getFingerprint(this);
          }
          this._updatePending = false;
        });
      }

      render() {
        const api = Attr(this).get("api");
        if (!api || !apiHandlers[api]) return logError(this, `Unknown or missing api: ${api}`);
        try {
          const slots = getSlots(this);
          const newChildren = [...slots, ...apiHandlers[api](this, slots)];
          getHtmls(this.children) !== getHtmls(newChildren) && this.replaceChildren(...newChildren);
        } catch (error) {
          logError(this, `Failed ${api}: ${error.message}`);
        }
      }

      // VDOM compatibility for reordering APIs
      get childNodes() {
        if (!Attr(this).has("vdom")) return super.childNodes;
        const vdomSlotSort = (a, b) => {
          const slot = { a: Attr(a).get("slot"), b: Attr(b).get("slot") };
          return slot.a === slot.b ? 0 : slot.a < slot.b ? -1 : 1;
        };
        const patternMatch = {
          Collator: () => getSlots(this).sort(vdomSlotSort), // sort by slot name
          ListFormat: () => getSlots(this), // exclude literals
        };
        return patternMatch[Attr(this).get("api")]?.() ?? super.childNodes; // Other APIs: return real childNodes
      }

      // Access real childNodes if needed
      get realChildNodes() {
        return super.childNodes;
      }
    },
  );

  // Set up global lang observer on document root
  const rootObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.type !== "attributes" || mutation.attributeName !== "lang") return;
      const components = mutation.target.querySelectorAll(COMPONENT_NAME);
      components.forEach((component) => component.update?.());
    });
  });

  // Observe lang changes on entire document
  rootObserver.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ["lang"],
    subtree: true,
  });
};

export default { init };
